/**
 * Common field wrapper for consistent layout and error display.
 * Provides ARIA attributes for accessibility.
 */

import { useTranslation } from "react-i18next";
import { Label } from "~/components/ui/label";
import { AlertCircle, Info, HelpCircle } from "lucide-react";
import type { FieldWrapperProps } from "./types";

/**
 * Generates consistent ARIA IDs for field accessibility
 */
export function getFieldAriaIds(name: string) {
  return {
    descriptionId: `${name}-description`,
    extendedDescriptionId: `${name}-extended-description`,
    errorId: `${name}-error`,
  };
}

export function FieldWrapper({
  name,
  label,
  required,
  hasError,
  error,
  description,
  extendedDescription,
  children,
  hideLabel = false,
}: FieldWrapperProps) {
  const { t } = useTranslation();
  const { descriptionId, extendedDescriptionId, errorId } = getFieldAriaIds(name);

  return (
    <div className="space-y-2 animate-in">
      {!hideLabel && (
        <Label htmlFor={name} required={required}>
          {label}
        </Label>
      )}

      {children}

      {description && !hasError && (
        <p
          id={descriptionId}
          className="text-sm text-muted-foreground flex items-center gap-1"
        >
          <Info className="h-3 w-3" aria-hidden="true" />
          {description}
        </p>
      )}

      {extendedDescription && !hasError && (
        <details className="text-sm">
          <summary className="text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1 select-none">
            <HelpCircle className="h-3 w-3" aria-hidden="true" />
            {t("common.moreInfo", "More information")}
          </summary>
          <p
            id={extendedDescriptionId}
            className="mt-2 text-muted-foreground pl-4 border-l-2 border-muted"
          >
            {extendedDescription}
          </p>
        </details>
      )}

      {hasError && error && (
        <p
          id={errorId}
          role="alert"
          aria-live="polite"
          className="text-sm text-destructive flex items-center gap-1"
        >
          <AlertCircle className="h-3 w-3" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}
